import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Card, List, Tag, Button, Empty, Flex, Typography } from "antd";
import {
  DesktopOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BellOutlined,
  TagsOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  UserDeleteOutlined,
  ScheduleOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { useNavigate } from "react-router-dom";
import PageHeader from "./common/PageHeader";
import StatCard from "./common/StatCard";
import InfoHint from "./common/InfoHint";
import { TRAFFIC } from "../theme/themeConfig";
import { useAuth } from "../context/AuthContext";
import { getFleetHealth, getAlerts, getTickets, listVisits } from "../network/api";
import { notify, serverError } from "../utils/notify";
import { HEALTH_STATE_LABEL, HEALTH_STATE_TAG_COLOR, relativeTime } from "../constants/health";
import { TICKET_STATUS_LABEL, TICKET_STATUS_TAG_COLOR } from "../constants/tickets";
import { VISIT_TYPE_LABEL, VISIT_TYPE_TAG_COLOR, VISIT_STATUS_LABEL, VISIT_STATUS_TAG_COLOR } from "../constants/visits";

const { Text } = Typography;
const OPEN_TICKET_STATUSES = ["open", "assigned", "in_progress", "resolved"];
const OPEN_VISIT_STATUSES = ["scheduled", "in_progress"];

// ---------------------------------------------------------------------------
// Technician dashboard — their own queue only (no fleet-wide data).
// ---------------------------------------------------------------------------
function TechnicianHome({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [visits, setVisits] = useState([]);
  const [tickets, setTickets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vi, tk] = await Promise.all([
        listVisits({ mine: true, pageSize: 100 }),
        getTickets({ mine: true }),
      ]);
      setVisits((vi.data || []).filter((v) => OPEN_VISIT_STATUSES.includes(v.status)));
      setTickets((tk.tickets || []).filter((t) => OPEN_TICKET_STATUSES.includes(t.status)));
    } catch (e) {
      notify.error(serverError(e, "Failed to load your dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inProgress = visits.filter((v) => v.status === "in_progress").length;

  return (
    <div>
      <PageHeader
        title={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
        subtitle="Your visits & assigned tickets"
        extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>}
      />
      <Row gutter={[16, 16]}>
        <Col xs={12} md={8}>
          <StatCard title="My Open Visits" value={visits.length} loading={loading}
            tooltip="Site visits assigned to you that are scheduled or in progress. Click to see the full list."
            icon={<ScheduleOutlined />} accent="#2f54eb" onClick={() => navigate("/dashboard/visits?mine=true")} />
        </Col>
        <Col xs={12} md={8}>
          <StatCard title="In Progress" value={inProgress} loading={loading}
            tooltip="Visits you have already started but not yet marked complete."
            icon={<ClockCircleOutlined />} accent={TRAFFIC.yellow} onClick={() => navigate("/dashboard/visits?mine=true")} />
        </Col>
        <Col xs={12} md={8}>
          <StatCard title="My Open Tickets" value={tickets.length} loading={loading}
            tooltip="Repair tickets assigned to you that are still open, assigned, in progress or resolved (not yet closed)."
            icon={<TagsOutlined />} accent="#722ed1" onClick={() => navigate("/dashboard/tickets?mine=true")} />
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<span>My Visits<InfoHint title="Your upcoming and active site visits. Click any row to open its details and checklist." /></span>} extra={<Button type="link" onClick={() => navigate("/dashboard/visits?mine=true")}>View all <ArrowRightOutlined /></Button>} style={{ height: "100%" }}>
            <List
              dataSource={visits.slice(0, 6)}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open visits 🎉" /> }}
              renderItem={(v) => (
                <List.Item style={{ cursor: "pointer" }} onClick={() => navigate(`/dashboard/visit-detail/${v._id}`)}
                  actions={[<Tag color={VISIT_STATUS_TAG_COLOR[v.status]}>{VISIT_STATUS_LABEL[v.status] || v.status}</Tag>]}>
                  <List.Item.Meta
                    avatar={<ScheduleOutlined style={{ fontSize: 18, color: "#2f54eb" }} />}
                    title={<Flex gap={8} align="center"><Tag color={VISIT_TYPE_TAG_COLOR[v.visitType]}>{VISIT_TYPE_LABEL[v.visitType] || v.visitType}</Tag><Text>{v.shop?.code || v.deviceId || "—"}</Text></Flex>}
                    description={v.scheduledAt ? `Scheduled ${relativeTime(v.scheduledAt)}` : undefined}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<span>My Tickets<InfoHint title="Repair tickets assigned to you. Click a ticket to view the fault, device and history." /></span>} extra={<Button type="link" onClick={() => navigate("/dashboard/tickets?mine=true")}>View all <ArrowRightOutlined /></Button>} style={{ height: "100%" }}>
            <List
              dataSource={tickets.slice(0, 6)}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No assigned tickets" /> }}
              renderItem={(t) => (
                <List.Item style={{ cursor: "pointer" }} onClick={() => navigate(`/dashboard/tickets/${t._id}`)}
                  actions={[<Tag color={TICKET_STATUS_TAG_COLOR[t.status]}>{TICKET_STATUS_LABEL[t.status] || t.status}</Tag>]}>
                  <List.Item.Meta
                    avatar={<TagsOutlined style={{ fontSize: 18, color: "#722ed1" }} />}
                    title={t.title}
                    description={t.deviceId}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manager dashboard — supervisor / hardwareAdmin / superAdmin (fleet view).
// ---------------------------------------------------------------------------
function ManagerHome({ user, isSupervisor, isHardwareAdmin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, byColor: {}, byState: {} });
  const [alerts, setAlerts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [verifyQueue, setVerifyQueue] = useState(null); // supervisor: items awaiting sign-off
  const [ops, setOps] = useState(null); // hardware-admin operations counts

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [health, al, tk] = await Promise.all([
        getFleetHealth(),
        getAlerts({ status: "open" }),
        getTickets(),
      ]);
      setSummary(health.summary || { total: 0, byColor: {}, byState: {} });
      setAlerts((al.alerts || []).slice(0, 6));
      setTickets((tk.tickets || []).filter((t) => OPEN_TICKET_STATUSES.includes(t.status)));

      // Supervisor sign-off queue: tickets resolved (awaiting verify) + completed visits.
      if (isSupervisor) {
        const [resolvedTk, completedVi] = await Promise.all([
          getTickets({ status: "resolved" }),
          listVisits({ status: "completed", pageSize: 1 }),
        ]);
        setVerifyQueue({
          ticketsToVerify: resolvedTk.count ?? (resolvedTk.tickets || []).length,
          visitsCompleted: completedVi.total || 0,
        });
      }

      // Operations oversight (hardware-admin): work that needs attention.
      if (isHardwareAdmin) {
        const [unTk, unVi, ovVi] = await Promise.all([
          getTickets({ status: "open", unassigned: true }),
          listVisits({ unassigned: true, status: "scheduled", pageSize: 1 }),
          listVisits({ overdue: true, pageSize: 1 }),
        ]);
        setOps({
          unassignedTickets: unTk.count ?? (unTk.tickets || []).length,
          unassignedVisits: unVi.total || 0,
          overdueVisits: ovVi.total || 0,
        });
      }
    } catch (e) {
      notify.error(serverError(e, "Failed to load dashboard"));
    } finally {
      setLoading(false);
    }
  }, [isSupervisor, isHardwareAdmin]);

  useEffect(() => { load(); }, [load]);

  const byColor = summary.byColor || {};
  const donut = [
    { name: "Healthy", value: byColor.green || 0, color: TRAFFIC.green },
    { name: "Warning", value: byColor.yellow || 0, color: TRAFFIC.yellow },
    { name: "Critical", value: byColor.red || 0, color: TRAFFIC.red },
  ];
  const evaluated = donut.reduce((a, d) => a + d.value, 0);

  return (
    <div>
      <PageHeader
        title={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
        subtitle={isHardwareAdmin ? "Fleet overview & operations" : "Fleet overview & field oversight"}
        extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>}
      />

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <StatCard title="Total Devices" value={summary.total} loading={loading}
            tooltip="Every edge device registered in the fleet, regardless of health. Click to open the device list."
            icon={<DesktopOutlined />} accent="#2f54eb" onClick={() => navigate("/dashboard/devices")} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard title="Healthy" value={byColor.green || 0} loading={loading}
            tooltip="Devices reporting normally with no active alerts (green status)."
            icon={<CheckCircleOutlined />} accent={TRAFFIC.green}
            valueStyle={{ color: TRAFFIC.green }} onClick={() => navigate("/dashboard/health")} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard title="Needs Attention" value={(byColor.yellow || 0) + (byColor.red || 0)} loading={loading}
            tooltip="Devices in warning (yellow) or critical (red) state. Click to triage them in Fleet Health."
            icon={<WarningOutlined />} accent={TRAFFIC.red}
            valueStyle={{ color: TRAFFIC.red }} onClick={() => navigate("/dashboard/health")} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard title="Open Tickets" value={tickets.length} loading={loading}
            tooltip="Repair tickets across the whole fleet that are not yet closed."
            icon={<TagsOutlined />} accent="#722ed1" onClick={() => navigate("/dashboard/tickets")} />
        </Col>

        {/* Field oversight — supervisor+ (their verification queue + team). */}
        {verifyQueue && (
          <>
            <Col span={24} style={{ marginTop: 4, marginBottom: -4 }}>
              <Text strong style={{ fontSize: 13, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 0.5 }}>Field oversight</Text>
              <InfoHint title="Your supervisor queue: work that technicians have finished and that now needs your review or sign-off." />
            </Col>
            <Col xs={12} md={6}>
              <StatCard title="Tickets to Verify" value={verifyQueue.ticketsToVerify} loading={loading}
                tooltip="Tickets a technician marked resolved that are waiting for you to verify the fix and close them."
                icon={<SafetyCertificateOutlined />} accent="#13c2c2"
                valueStyle={{ color: verifyQueue.ticketsToVerify ? "#08979c" : undefined }}
                onClick={() => navigate("/dashboard/tickets?status=resolved")} />
            </Col>
            <Col xs={12} md={6}>
              <StatCard title="Completed Visits" value={verifyQueue.visitsCompleted} loading={loading}
                tooltip="Site visits technicians have marked complete. Review their checklists and photos."
                icon={<CheckCircleOutlined />} accent={TRAFFIC.green}
                onClick={() => navigate("/dashboard/visits?status=completed")} />
            </Col>
            <Col xs={12} md={6}>
              <StatCard title="Team Scorecard" value="View" loading={loading}
                tooltip="Per-technician performance: visits completed, tickets resolved and turnaround times."
                icon={<TrophyOutlined />} accent="#faad14" onClick={() => navigate("/dashboard/team")} />
            </Col>
            <Col xs={12} md={6} />
          </>
        )}

        {/* Operations oversight — hardware-admin only. */}
        {ops && (
          <>
            <Col span={24} style={{ marginTop: 4, marginBottom: -4 }}>
              <Text strong style={{ fontSize: 13, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 0.5 }}>Needs attention</Text>
              <InfoHint title="Operational gaps to clear: work with nobody assigned or already past due. Aim to keep these at zero." />
            </Col>
            <Col xs={12} md={6}>
              <StatCard title="Unassigned Tickets" value={ops.unassignedTickets} loading={loading}
                tooltip="Open tickets that have no technician assigned yet. Assign them so the fault gets worked."
                icon={<UserDeleteOutlined />} accent={TRAFFIC.red}
                valueStyle={{ color: ops.unassignedTickets ? TRAFFIC.red : undefined }}
                onClick={() => navigate("/dashboard/tickets?unassigned=true")} />
            </Col>
            <Col xs={12} md={6}>
              <StatCard title="Unassigned Visits" value={ops.unassignedVisits} loading={loading}
                tooltip="Scheduled site visits with no technician assigned yet."
                icon={<ScheduleOutlined />} accent={TRAFFIC.yellow}
                valueStyle={{ color: ops.unassignedVisits ? "#d48806" : undefined }}
                onClick={() => navigate("/dashboard/visits?unassigned=true")} />
            </Col>
            <Col xs={12} md={6}>
              <StatCard title="Overdue Visits" value={ops.overdueVisits} loading={loading}
                tooltip="Scheduled visits whose due date has already passed. Reschedule or reassign them."
                icon={<ClockCircleOutlined />} accent={TRAFFIC.red}
                valueStyle={{ color: ops.overdueVisits ? TRAFFIC.red : undefined }}
                onClick={() => navigate("/dashboard/visits?overdue=true")} />
            </Col>
            <Col xs={12} md={6} />
          </>
        )}

        {/* Fleet health donut */}
        <Col xs={24} lg={10}>
          <Card title={<span>Fleet Health<InfoHint title="How the fleet splits across health states — Healthy (green), Warning (yellow) and Critical (red). The centre number is the total device count." /></span>} extra={<Button type="link" onClick={() => navigate("/dashboard/health")}>View all <ArrowRightOutlined /></Button>} style={{ height: "100%" }}>
            {evaluated === 0 ? (
              <Empty description="No devices evaluated yet" />
            ) : (
              <Flex align="center" gap={16} wrap>
                <div style={{ width: 200, height: 200, position: "relative" }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={donut} dataKey="value" innerRadius={62} outerRadius={92} paddingAngle={2} stroke="none">
                        {donut.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <RTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Flex vertical align="center" justify="center"
                    style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                    <Text style={{ fontSize: 28, fontWeight: 700 }}>{summary.total}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>devices</Text>
                  </Flex>
                </div>
                <Flex vertical gap={10} style={{ flex: 1, minWidth: 140 }}>
                  {donut.map((d) => (
                    <Flex key={d.name} justify="space-between" align="center">
                      <Flex align="center" gap={8}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: d.color }} />
                        <Text>{d.name}</Text>
                      </Flex>
                      <Text strong>{d.value}</Text>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            )}
          </Card>
        </Col>

        {/* Recent open alerts */}
        <Col xs={24} lg={14}>
          <Card title={<span>Recent Alerts<InfoHint title="The latest open health alerts raised by devices — e.g. offline, no captures, or sensor faults. Click one to open that device's health." /></span>} extra={<Button type="link" onClick={() => navigate("/dashboard/health")}>View all <ArrowRightOutlined /></Button>} style={{ height: "100%" }}>
            <List
              dataSource={alerts}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open alerts 🎉" /> }}
              renderItem={(a) => (
                <List.Item
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/dashboard/health/${a.deviceId}`)}
                  actions={[<Text type="secondary" style={{ fontSize: 12 }}>{relativeTime(a.openedAt)}</Text>]}
                >
                  <List.Item.Meta
                    avatar={<BellOutlined style={{ color: a.severity === "red" ? TRAFFIC.red : TRAFFIC.yellow, fontSize: 18 }} />}
                    title={<Flex gap={8} align="center"><Tag color={HEALTH_STATE_TAG_COLOR[a.state] || "default"}>{HEALTH_STATE_LABEL[a.state] || a.type}</Tag><Text>{a.deviceId}</Text></Flex>}
                    description={a.message}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default function Home() {
  const { user, isSupervisor, isHardwareAdmin } = useAuth();
  // Technicians get a focused personal queue; supervisors and admins get the
  // fleet view (with role-specific oversight cards layered on).
  if (!isSupervisor) return <TechnicianHome user={user} />;
  return <ManagerHome user={user} isSupervisor={isSupervisor} isHardwareAdmin={isHardwareAdmin} />;
}
